import React from 'react';
import { useLang } from './i18n.js';
import { shiftPeriod, periodLabel } from './money.js';

/**
 * Primitives.
 *
 * The design expresses everything as inline styles on tokens, so these
 * components carry those exact values and lean on app.css only where CSS is
 * genuinely required (hover, focus, breakpoints, drag states).
 */

/** Entity mark: BTM is a dashed ring with a dot, MR a solid ring. Straight from
 *  the two logos, and the reason a mixed list is never ambiguous at a glance. */
export function EntityGlyph({ entity, size = 10 }) {
  const btm = entity === 'BTM';
  const color = btm ? 'var(--entity-btm)' : 'var(--entity-mr)';
  return (
    <span
      aria-label={entity}
      title={entity}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        border: btm ? `1.5px dashed ${color}` : `${Math.max(2, size / 3.3)}px solid ${color}`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {btm && (
        <span
          style={{ width: size * 0.35, height: size * 0.35, borderRadius: '50%', background: color }}
        />
      )}
    </span>
  );
}

export const entityColor = (e) => (e === 'BTM' ? 'var(--entity-btm)' : 'var(--entity-mr)');

const AVATAR_BG = [
  'var(--btm-purple)',
  'var(--btm-magenta)',
  'var(--mr-violet)',
  'var(--mr-mid)',
  'var(--btm-coral)',
  'var(--mr-cyan-deep)',
];

export function Avatar({ initials, seed = 1, size = 24, title }) {
  return (
    <span
      title={title}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: AVATAR_BG[(Number(seed) || 1) % AVATAR_BG.length],
        color: '#fff',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.38,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {initials || '?'}
    </span>
  );
}

export function Kpi({ label, value, delta, deltaColor }) {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {delta && (
        <div style={{ fontSize: 11, fontWeight: 700, color: deltaColor || 'var(--ink-3)' }}>
          {delta}
        </div>
      )}
    </div>
  );
}

export function Card({ title, action, children, style }) {
  return (
    <div className="card" style={style}>
      {(title || action) && (
        <div className="card-head">
          <span className="card-title">{title}</span>
          {action && <span style={{ marginLeft: 'auto' }}>{action}</span>}
        </div>
      )}
      {children}
    </div>
  );
}

export const Empty = ({ children }) => <div className="empty">{children}</div>;

export function Spinner({ label }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: 28,
        color: 'var(--ink-3)',
        fontSize: 13,
      }}
    >
      <span className="spinner" />
      {label}
    </div>
  );
}

export function Segmented({ value, options, onChange }) {
  return (
    <div className="seg">
      {options.map((o) => (
        <button
          key={o.value}
          className={o.value === value ? 'is-on' : ''}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Chip({ on, onClick, children, title }) {
  return (
    <button className={`chip${on ? ' is-on' : ''}`} onClick={onClick} title={title}>
      {children}
    </button>
  );
}

export function Tier({ tier }) {
  if (!tier) return null;
  const color =
    tier === 'A' ? 'var(--success)' : tier === 'B' ? 'var(--warning)' : 'var(--ink-3)';
  return (
    <span
      title={`Tier ${tier}`}
      style={{
        width: 7,
        height: 7,
        borderRadius: '50%',
        background: tier === 'C' ? 'transparent' : color,
        border: `1.5px solid ${color}`,
        flexShrink: 0,
      }}
    />
  );
}

/** Key/value list used across both drawers' Overview tabs. */
export function Facts({ rows }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', rowGap: 9, fontSize: 13 }}>
      {rows
        .filter((r) => r)
        .map(([k, v]) => (
          <React.Fragment key={k}>
            <span style={{ color: 'var(--ink-3)' }}>{k}</span>
            <span style={{ fontWeight: 500, minWidth: 0 }}>{v ?? '—'}</span>
          </React.Fragment>
        ))}
    </div>
  );
}

/** Backdrop + panel. Escape closes; the scrim is clickable. */
export function Overlay({ onClose, children, kind = 'drawer' }) {
  React.useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className={kind} role="dialog" aria-modal="true">
        {children}
      </div>
    </>
  );
}

export function Toast({ message }) {
  if (!message) return null;
  return <div className="toast">{message}</div>;
}

/**
 * ‹ August 2026 › — the month picker shared by Monthly Review, Insights and the
 * Admin quota grid.
 *
 * It will not step past `max` (the current Bangkok month) because every one of
 * those endpoints rejects a future period with a 400. Disabling the arrow says
 * so before the request does.
 */
export function PeriodStepper({ period, onChange, max, width = 132 }) {
  const { t, lang } = useLang();
  const atMax = period >= max;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <button
        className="icon-btn"
        title={t('prevMonth')}
        onClick={() => onChange(shiftPeriod(period, -1))}
      >
        ‹
      </button>
      <span
        className="display"
        style={{ fontSize: 14, minWidth: width, textAlign: 'center', whiteSpace: 'nowrap' }}
      >
        {periodLabel(period, lang)}
      </span>
      <button
        className="icon-btn"
        title={t('nextMonth')}
        disabled={atMax}
        style={atMax ? { opacity: 0.35, cursor: 'not-allowed' } : undefined}
        onClick={() => !atMax && onChange(shiftPeriod(period, 1))}
      >
        ›
      </button>
    </div>
  );
}

/** Progress bar split into new-customer and upsell segments, as the design
 *  draws attainment — the two halves are quota'd separately. */
export function SplitBar({ newPct, upPct }) {
  return (
    <div
      style={{
        height: 10,
        borderRadius: 5,
        background: 'var(--sunken)',
        overflow: 'hidden',
        display: 'flex',
      }}
    >
      <span style={{ width: `${newPct}%`, background: 'var(--primary)' }} />
      <span style={{ width: `${upPct}%`, background: 'var(--mr-cyan-deep)' }} />
    </div>
  );
}
